import { useDebounce } from '@mm/hooks';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import {
  MyLeadsFilterTypes,
  MyleadsRelationalOperators,
  MyleadsRelationalOperatorType,
  MyLeadsSearchQuery,
} from '@mm/state/interfaces/myLeads';
import {
  ConfigProvider,
  InputNumber,
  InputNumberProps,
  Radio,
  RadioChangeEvent,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { setPlaceholderByKey } from '../helpers';
import styles from '../MyLeadsFiltersPanel.module.css';
import { useFilterParams } from '../use-filter-params';

type Props = {
  attributeKey: string;
  title: string;
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  useExtendedOptions?: boolean;
  maxInputValue?: number;
  inputProps?: InputNumberProps<number>;
};

export const RadioGroup = ({
  attributeKey,
  title,
  filterParams,
  setFilterParams,
  useExtendedOptions,
  maxInputValue,
  inputProps,
}: Props) => {
  const defaultOptions = [
    { name: 'More than', value: MyleadsRelationalOperators.GREATER_THAN },
    { name: 'Less than', value: MyleadsRelationalOperators.LESS_THAN },
  ];
  const extendedOptions = [
    ...defaultOptions,
    { name: 'Equals', value: MyleadsRelationalOperators.EQUAL },
  ];
  const options = useExtendedOptions ? extendedOptions : defaultOptions;
  const currentValue = filterParams.query?.attributes?.[attributeKey];
  const defaults = Object.entries(currentValue?.[0] || {}).flat();
  const defaultOperator = defaults?.[0] as MyleadsRelationalOperatorType;
  const defaultInputValue = defaults?.[1] as string;

  const defaultGroupValue = options[0].value;
  const [groupValue, setGroupValue] = useState<string>(
    defaultOperator || defaultGroupValue,
  );
  const [inputValue, setInputValue] = useState<string | null>(
    defaultInputValue || null,
  );

  const debouncedInputValue = useDebounce(inputValue?.toString(), 500);

  const { getUpdatedFilterParams, shouldDisableFilters } = useFilterParams({
    filterParams,
  });
  const { primaryColor, isWhitelabelCompany } = useWhitelabelConfig();

  const updateFilterParams = (group?: string) => {
    if (currentValue) {
      delete filterParams.query?.attributes?.[attributeKey];
    }

    const radioGroup = group ? group : groupValue;

    const updatedQuery = getUpdatedFilterParams({
      key: attributeKey,
      value:
        (radioGroup === 'eq'
          ? debouncedInputValue
          : { [radioGroup]: debouncedInputValue }) || '',
      isExisting: !debouncedInputValue,
      type: MyLeadsFilterTypes.attributes,
    });
    const newValue = updatedQuery?.query?.attributes?.[attributeKey];

    if (currentValue !== newValue) {
      setFilterParams(updatedQuery);
    }
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    const { value } = e.target || {};
    setGroupValue(value);

    if (debouncedInputValue) {
      updateFilterParams(value);
    }
  };

  const onInputChange = (value: string | null) => {
    setInputValue(value);
  };

  useEffect(() => {
    updateFilterParams();
  }, [debouncedInputValue]);

  useEffect(() => {
    if (!currentValue) {
      setInputValue(null);
      setGroupValue(defaultGroupValue);
    }
  }, [currentValue]);

  return (
    <div className={styles.radioGroupWrapper}>
      <div className={styles.heading}>{title}</div>

      <Radio.Group onChange={onRadioChange} value={groupValue}>
        {options.map(option => {
          const isRadioChecked = groupValue === option.value;
          return (
            <div
              className={styles.radioGroupRow}
              key={`${attributeKey}-${option.value}`}
            >
              <ConfigProvider
                theme={{
                  components: {
                    Radio: {
                      colorPrimary: isWhitelabelCompany
                        ? primaryColor
                        : 'var(--color-geekblue-6)',
                    },
                  },
                }}
              >
                <Radio
                  defaultChecked={isRadioChecked}
                  value={option.value}
                  className={styles.radio}
                  disabled={shouldDisableFilters}
                >
                  {option.name}
                </Radio>
              </ConfigProvider>

              {isRadioChecked && (
                <InputNumber
                  className={styles.numberInput}
                  min={1}
                  max={maxInputValue}
                  value={inputValue}
                  maxLength={6}
                  onChange={onInputChange}
                  disabled={shouldDisableFilters}
                  placeholder={setPlaceholderByKey(attributeKey)}
                  {...(inputProps || {})}
                />
              )}
            </div>
          );
        })}
      </Radio.Group>
    </div>
  );
};
